import { Injectable, OnDestroy, inject } from '@angular/core';
import { App } from '@df/core/app';
import { isPrerenderOld } from '@df/core/service/client';
import { WINDOW } from '@df/dom/globals';
import { gtmAwait } from '@df/module-gtm/integration/gtm-inclusion';
import { NDfPrivacy } from '@df/module-privacy/df-module-privacy.interfaces';
import { PrivacyService } from '@df/module-privacy/privacy.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { NDfModuleOnetrust } from './df-module-onetrust.interfaces';

@Injectable({
    providedIn: 'root'
})
export class OneTrustService implements OnDestroy {
    private window = inject(WINDOW);
    private privacyService = inject(PrivacyService);
    private app = inject(App);
    private timeoutService = inject(TimeoutService);
    private config = inject(NDfModuleOnetrust.OPTIONS);

    async appInitialize() {
        if (this.app.isPlatformBrowser) {
            await init();
            this.window.addEventListener('OneTrustGroupsUpdated', (event: NDfModuleOnetrust.IGroupEvent) => this.onGroupsUpdated(event));
        }
    }

    ngOnDestroy(): void {
        if (this.app.isPlatformBrowser) {
            this.window.removeEventListener('OneTrustGroupsUpdated', (event: NDfModuleOnetrust.IGroupEvent) => this.onGroupsUpdated(event));
        }
    }

    toggleInfoDisplay(): void {
        if (this.app.isPlatformBrowser) {
            window.OneTrust?.ToggleInfoDisplay();
        }
    }

    loadBanner(): void {
        if (this.app.isPlatformBrowser) {
            window.OneTrust?.Init();
            this.timeoutService.setTimeout(() => window.OneTrust?.LoadBanner(), 1000);
        }
    }

    /**
     * Sync privacy data with OneTrust
     */
    private onGroupsUpdated(event: NDfModuleOnetrust.IGroupEvent): void {
        if (Array.isArray(event?.detail)) {
            if (!this.config.groups) {
                return;
            }

            const configGroups = [...this.config.groups];
            const data: NDfPrivacy.IPrivacyData = {};

            event.detail.forEach(id => {
                const groupIndex = configGroups?.findIndex(group => (Array.isArray(group.id) ? group.id.includes(id) : group.id === id));

                if (groupIndex !== -1) {
                    const group = configGroups[groupIndex];

                    const area = this.privacyService.areas[group.area];

                    if (area !== undefined) {
                        data[group.area] = true;
                        configGroups.splice(groupIndex, 1);
                    }
                }
            });

            // set all other areas to `false` (without `core` that should be always `true`)
            configGroups.forEach(group => {
                const area = this.privacyService.areas[group.area];

                if (area !== undefined && group.area !== NDfPrivacy.AREA.core) {
                    data[group.area] = false;
                }
            });

            this.privacyService.save(data);
        }
    }
}

let initPromise: Promise<void> | undefined;

const config = typeof window !== 'undefined' ? window.dfConfig.oneTrust : undefined;

function init(): Promise<void> {
    if (!initPromise) {
        initPromise = load();
        gtmAwait(done => initPromise!.finally(done));
    }

    return initPromise;
}

async function load() {
    if (!config || !config.id || !config.enabled || typeof document === 'undefined' || isPrerenderOld) {
        return;
    }

    if (config.autoBlock && config.autoBlockEnabled) {
        await loadScript(config.autoBlock.replace('{{ID}}', config.id));
    }

    const textScript = await loadScript();
    textScript.text = 'function OptanonWrapper() { }';

    await loadScript(config.sdk, textScript, { dataset: { documentLanguage: true, domainScript: config.id } });
}

function loadScript(url?: string, afterElement?: HTMLScriptElement, options?: { dataset?: any }): Promise<HTMLScriptElement> {
    return new Promise<HTMLScriptElement>((resolve, reject) => {
        if (typeof document === 'undefined') {
            return reject('Not a browser env');
        }
        const script = document.createElement('script');
        if (url) {
            if (options?.dataset) {
                for (const key in options.dataset) {
                    script.dataset[key] = options.dataset[key];
                }
            }
            script.async = true;
            script.src = url;
            script.type = 'text/javascript';
            script.onload = () => resolve(script);
            script.onerror = reject;
        }
        if (afterElement) {
            afterElement.parentNode!.insertBefore(script, afterElement.nextSibling);
        } else {
            document.head.prepend(script, document.getElementsByTagName('script')[0]);
        }
        if (!url) {
            resolve(script);
        }
    });
}
